import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

interface Measurement {
  id: string;
  measured_at: string;
  weight_lbs: number | null;
  body_fat_percent: number | null;
  waist_inches: number | null;
}

interface MeasurementChartProps {
  measurements: Measurement[];
  metric: "weight" | "body_fat" | "waist";
}

const metricConfig = {
  weight: {
    key: "weight_lbs",
    label: "Weight",
    unit: "lbs",
    color: "hsl(var(--chart-2))",
  },
  body_fat: {
    key: "body_fat_percent",
    label: "Body Fat",
    unit: "%",
    color: "hsl(var(--chart-1))",
  },
  waist: {
    key: "waist_inches",
    label: "Waist",
    unit: "in",
    color: "hsl(var(--chart-3))",
  },
};

const MeasurementChart = ({ measurements, metric }: MeasurementChartProps) => {
  const config = metricConfig[metric];

  const chartData = useMemo(() => {
    return measurements
      .filter((m) => m[config.key as keyof Measurement] !== null)
      .map((m) => ({
        date: format(new Date(m.measured_at), "MMM d"),
        value: m[config.key as keyof Measurement] as number,
        fullDate: format(new Date(m.measured_at), "MMM d, yyyy"),
      }))
      .reverse(); // Oldest first for chart
  }, [measurements, config.key]);

  if (chartData.length < 2) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            {config.label} Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="chart-height-sm flex items-center justify-center text-muted-foreground text-sm">
            Need at least 2 measurements to show trend
          </div>
        </CardContent>
      </Card>
    );
  }

  const minValue = Math.min(...chartData.map((d) => d.value));
  const maxValue = Math.max(...chartData.map((d) => d.value));
  const padding = (maxValue - minValue) * 0.1 || 5;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {config.label} Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="chart-height-sm" role="img" aria-label={`${config.label} trend over time`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                domain={[minValue - padding, maxValue + padding]}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                width={45}
                tickFormatter={(value) => `${value}${config.unit === "%" ? "%" : ""}`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="text-xs text-muted-foreground">
                          {data.fullDate}
                        </div>
                        <div className="font-semibold">
                          {data.value} {config.unit}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={config.color}
                strokeWidth={2}
                dot={{ fill: config.color, strokeWidth: 0 }}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default MeasurementChart;
